import React from 'react';
import { Modal, Row, Col, Card, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faLightbulb,
  faEnvelope,
  faRocket,
  faCheckCircle,
  faNetworkWired,
  faArrowUp,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { useEmailAnalytics } from 'hooks/useEmailAnalytics';
import { formatErrorForReact } from 'helpers/utils';

interface EmailIntelligenceModalProps {
  show: boolean;
  onHide: () => void;
  selectedEmail: { email: string } | null;
}

const EmailIntelligenceModal: React.FC<EmailIntelligenceModalProps> = ({
  show,
  onHide,
  selectedEmail,
}) => {
  const { analytics, loading, error } = useEmailAnalytics(selectedEmail?.email || '');

  const renderAnalyticsCard = (
    icon: any,
    value: string | number,
    label: string,
    colorClass: string,
    bgColor: string,
    isLoading: boolean = false
  ) => (
    <Card className="text-center border-0 h-100" style={{ background: bgColor }}>
      <Card.Body className="d-flex flex-column justify-content-center">
        <FontAwesomeIcon icon={icon} className={`${colorClass} mb-2`} size="2x" />
        {isLoading ? (
          <Spinner animation="border" size="sm" className={colorClass} />
        ) : (
          <h4 className={colorClass}>{value}</h4>
        )}
        <small className="text-muted">{label}</small>
      </Card.Body>
    </Card>
  );

  const getEngagementTrendIcon = (trend: string) => {
    switch (trend) {
      case 'increasing':
        return { icon: faArrowUp, color: 'text-success', text: 'Increasing' };
      case 'decreasing':
        return { icon: faExclamationTriangle, color: 'text-warning', text: 'Decreasing' };
      default:
        return { icon: faCheckCircle, color: 'text-info', text: 'Stable' };
    }
  };

  const generateInsight = () => {
    if (!analytics) return "Loading AI insights...";
    
    const { responseRate, leadsIdentified, insights } = analytics;
    const trendInfo = getEngagementTrendIcon(insights.engagementTrend);
    
    if (responseRate > 80) {
      return `Excellent performance! Your ${responseRate}% response rate is outstanding. ${leadsIdentified} leads identified with ${trendInfo.text.toLowerCase()} engagement trend.`;
    } else if (responseRate > 60) {
      return `Good performance with ${responseRate}% response rate. Consider optimizing send times - your best performing hours are ${insights.topPerformingTimes.slice(0, 2).join(' and ')}.`;
    } else if (responseRate > 40) {
      return `Your ${responseRate}% response rate has room for improvement. Try sending emails during peak hours: ${insights.topPerformingTimes.join(', ')}.`;
    } else {
      return `Response rate of ${responseRate}% needs attention. Our AI suggests reviewing email content and timing optimization.`;
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header
        closeButton
        style={{
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
          border: 'none',
        }}
      >
        <Modal.Title className="d-flex align-items-center">
          <FontAwesomeIcon icon={faChartLine} className="me-2 text-primary" />
          Email Intelligence Dashboard
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: '2rem' }}>
        <div className="text-center mb-4">
          <FontAwesomeIcon
            icon={faLightbulb}
            size="3x"
            className="text-warning mb-3"
            style={{ filter: 'drop-shadow(0 0 8px rgba(245, 158, 11, 0.3))' }}
          />
          <h5>AI-Powered Email Analytics</h5>
          <p className="text-muted">
            For: <strong>{selectedEmail?.email}</strong>
          </p>
        </div>

        {error ? (
          <Alert variant="danger" className="text-center">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            <strong>Error Loading Analytics:</strong> {formatErrorForReact(error)}
          </Alert>
        ) : (
          <>
            <Row className="g-4">
              <Col md={6}>
                {renderAnalyticsCard(
                  faEnvelope,
                  analytics?.emailsProcessed || 0,
                  'Emails Processed',
                  'text-primary',
                  'rgba(59, 130, 246, 0.1)',
                  loading
                )}
              </Col>
              <Col md={6}>
                {renderAnalyticsCard(
                  faRocket,
                  analytics?.leadsIdentified || 0,
                  'Leads Identified',
                  'text-success',
                  'rgba(16, 185, 129, 0.1)',
                  loading
                )}
              </Col>
              <Col md={6}>
                {renderAnalyticsCard(
                  faCheckCircle,
                  analytics ? `${analytics.responseRate}%` : '0%',
                  'Response Rate',
                  'text-warning',
                  'rgba(245, 158, 11, 0.1)',
                  loading
                )}
              </Col>
              <Col md={6}>
                {renderAnalyticsCard(
                  faNetworkWired,
                  analytics?.avgResponseTime || 'N/A',
                  'Avg Response Time',
                  'text-danger',
                  'rgba(239, 68, 68, 0.1)',
                  loading
                )}
              </Col>
            </Row>

            {/* Additional Analytics */}
            {analytics && (
              <Row className="g-4 mt-2">
                <Col md={4}>
                  <Card className="text-center border-0" style={{ background: 'rgba(139, 92, 246, 0.1)' }}>
                    <Card.Body>
                      <FontAwesomeIcon icon={faArrowUp} className="text-purple mb-2" size="lg" />
                      <div className="fw-bold text-purple">{analytics.sentEmails}</div>
                      <small className="text-muted">Sent</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="text-center border-0" style={{ background: 'rgba(6, 182, 212, 0.1)' }}>
                    <Card.Body>
                      <FontAwesomeIcon icon={faEnvelope} className="text-cyan mb-2" size="lg" />
                      <div className="fw-bold text-cyan">{analytics.receivedEmails}</div>
                      <small className="text-muted">Received</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="text-center border-0" style={{ background: 'rgba(34, 197, 94, 0.1)' }}>
                    <Card.Body>
                      <FontAwesomeIcon icon={faCheckCircle} className="text-success mb-2" size="lg" />
                      <div className="fw-bold text-success">{analytics.aiAnalysisRate}%</div>
                      <small className="text-muted">AI Analyzed</small>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            <Alert variant="info" className="mt-4">
              <FontAwesomeIcon icon={faLightbulb} className="me-2" />
              <strong>AI Insight:</strong> {generateInsight()}
            </Alert>

            {/* Top Performing Times */}
            {analytics?.insights?.topPerformingTimes && analytics.insights.topPerformingTimes.length > 0 && (
              <Alert variant="success" className="mt-3">
                <FontAwesomeIcon icon={faArrowUp} className="me-2" />
                <strong>Optimal Send Times:</strong> {analytics.insights.topPerformingTimes.join(', ')}
              </Alert>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default EmailIntelligenceModal; 